import { XClose } from '@untitled-ui/icons-react/build/cjs';
import React, { memo, useEffect, useRef, useState } from 'react';
import ButtonCmp from 'src/components/ButtonCmp';

import AiOrb, { type AiOrbAgent } from '../AiOrb';

interface IProps {
    title: React.ReactNode;
    handleClose: () => void;
    children: React.ReactNode;
    footer?: React.ReactNode;
    agent?: AiOrbAgent | null;
    closeOnOutsideClick?: boolean;
    isEscapeClose?: boolean;
    primaryBtnText?: string;
    primaryBtnOnClick?: () => void;
    primaryBtnLoading?: boolean;
    primaryBtnDisabled?: boolean;
    primaryBtnIcon?: React.ReactNode;
    secondaryBtnText?: string;
    secondaryBtnOnClick?: () => void;
    secondaryBtnLoading?: boolean;
    secondaryBtnDisabled?: boolean;
    secondaryBtnIcon?: React.ReactNode;
    ContentWrapClassName?: string;
}

const modalStack: symbol[] = [];

const AiCommonModal = ({
    title,
    handleClose,
    children,
    footer,
    agent,
    closeOnOutsideClick = true,
    isEscapeClose = true,
    primaryBtnText,
    primaryBtnOnClick,
    primaryBtnLoading,
    primaryBtnDisabled,
    primaryBtnIcon,
    secondaryBtnText,
    secondaryBtnOnClick,
    secondaryBtnLoading,
    secondaryBtnDisabled,
    secondaryBtnIcon,
    ContentWrapClassName,
}: IProps) => {
    const modalIdRef = useRef(Symbol());
    const [isClosing, setIsClosing] = useState(false);

    useEffect(() => {
        modalStack.push(modalIdRef.current);

        return () => {
            const idx = modalStack.indexOf(modalIdRef.current);
            if (idx > -1) modalStack.splice(idx, 1);
        };
    }, []);

    const closeModal = () => {
        if (isClosing) return;

        setIsClosing(true);

        setTimeout(() => {
            handleClose();
        }, 320);
    };

    useEffect(() => {
        if (!isEscapeClose) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && modalStack[modalStack.length - 1] === modalIdRef.current) {
                closeModal();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => document.removeEventListener('keydown', handleKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEscapeClose, isClosing]);

    useEffect(() => {
        if (!primaryBtnOnClick) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Enter') return;
            if (modalStack[modalStack.length - 1] !== modalIdRef.current) return;
            if (primaryBtnLoading || primaryBtnDisabled || isClosing) return;

            e.preventDefault();
            primaryBtnOnClick();
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [primaryBtnOnClick, primaryBtnLoading, primaryBtnDisabled, isClosing]);

    return (
        <dialog className="modal modal-root !absolute !items-end" open>
            <div className={`absolute inset-0 bg-primarySolid bg-opacity-20 modal-overlay-animation ${isClosing ? 'close' : ''}`} onClick={closeOnOutsideClick ? closeModal : undefined} />

            <div
                className={`modal-box rounded-lg p-0 mx-3 my-5 flex flex-col shadow-[0_2px_7px_3px_rgba(16,24,40,0.08)] border border-utilityGray200 w-[calc(100%_-_24px)] max-w-[670px] !max-h-[65vh] modal-animation ${
                    isClosing ? 'close' : ''
                }`}
                onClick={(e) => e.stopPropagation()}
            >
                <header className="flex gap-x-2 items-center justify-between px-3 pt-3 pb-1 bg-white rounded-t-xl">
                    <AiOrb agent={agent} />

                    <span className="text-grayLight900 text-xs leading-[1.5] font-medium flex-1">{title}</span>

                    <button
                        type="button"
                        className="h-[18px] w-[18px] flex items-center justify-center rounded-full bg-transparent hover:bg-utilityGray100 transition-colors duration-200 flex-none"
                        onClick={closeModal}
                    >
                        <XClose className="w-3.5 h-3.5 text-grayLight600" />
                    </button>
                </header>

                <article className={`flex-1 overflow-y-auto custom-scrollbar-v2 p-3 text-textSecondary text-xs leading-[1.5] font-normal ${ContentWrapClassName}`}>{children}</article>

                {(footer || primaryBtnText || secondaryBtnText) && (
                    <footer className="px-3 pb-3 pt-1 bg-white rounded-b-xl flex items-center justify-between gap-2">
                        {footer && <div className="flex-1">{footer}</div>}
                        <div className={`flex items-center gap-2 justify-between ${footer ? 'w-fit' : 'w-full '}`}>
                            {secondaryBtnText && (
                                <ButtonCmp className="btn_secondary_black" onClick={secondaryBtnOnClick} loading={secondaryBtnLoading} disabled={secondaryBtnDisabled} icon={secondaryBtnIcon}>
                                    {secondaryBtnText}
                                </ButtonCmp>
                            )}

                            {primaryBtnText && (
                                <ButtonCmp
                                    iconSide="right"
                                    className="btn_secondary_black ml-auto"
                                    onClick={primaryBtnOnClick}
                                    loading={primaryBtnLoading}
                                    disabled={primaryBtnLoading || primaryBtnDisabled}
                                    icon={primaryBtnIcon}
                                >
                                    {primaryBtnText}
                                </ButtonCmp>
                            )}
                        </div>
                    </footer>
                )}
            </div>
        </dialog>
    );
};

export default memo(AiCommonModal);
